/**
 * Loan schemas.
 *
 * Note what is absent: there is no interest rate, no interest method, no
 * processing fee and no late-payment charge anywhere in this file. UNIFEMGA
 * lends on Qard Hasan terms, so a loan's total repayable is its principal. The
 * absence is the enforcement — there is no field for a future contributor to
 * populate by accident.
 */
import { z } from 'zod';
import { DisbursementStatus, EmploymentStatus, LoanApplicationStatus, LoanStatus, PaymentMethod, RepaymentFrequency, } from '../enums.js';
import { accountNumberSchema, emailSchema, isoDateSchema, membershipNumberSchema, nairaAmountSchema, pastDateSchema, phoneSchema, positiveNairaAmountSchema, searchSchema, uuidSchema, } from './common.js';
export const loanProductSchema = z
    .object({
    name: z.string().trim().min(3, 'Give the product a name.').max(100),
    description: z.string().trim().max(1000).optional(),
    /**
     * A minimum is optional — set it to 0 for no floor.
     *
     * Conventional lenders impose a minimum because processing cost outweighs
     * interest income on small loans. UNIFEMGA earns nothing on any loan, so
     * that rationale does not apply here, and a floor mainly serves to turn
     * away the member with the smallest need.
     */
    minimumAmount: nairaAmountSchema,
    maximumAmount: positiveNairaAmountSchema,
    minimumInstallments: z.coerce.number().int().min(1).max(600),
    maximumInstallments: z.coerce.number().int().min(1).max(600),
    repaymentFrequency: z.nativeEnum(RepaymentFrequency),
    /** Days after disbursement before the first repayment falls due. */
    gracePeriodDays: z.coerce.number().int().min(0).max(365).default(0),
    /** Installments are rounded to a multiple of this many naira. */
    roundingUnitNaira: z.coerce.number().int().min(1).max(100_000).default(1),
    minimumMembershipMonths: z.coerce.number().int().min(0).max(600).default(0),
    maxConcurrentLoans: z.coerce.number().int().min(1).max(10).default(1),
    maximumTotalExposure: nairaAmountSchema.nullable().optional(),
    requiredGuarantors: z.coerce.number().int().min(0).max(5).default(2),
    allowWithPriorDefault: z.boolean().default(false),
    eligibilityNotes: z.string().trim().max(2000).optional(),
    isActive: z.boolean().default(true),
})
    .refine((data) => data.maximumAmount >= data.minimumAmount, {
    message: 'Maximum amount must be at least the minimum amount.',
    path: ['maximumAmount'],
})
    .refine((data) => data.maximumInstallments >= data.minimumInstallments, {
    message: 'Maximum installments must be at least the minimum.',
    path: ['maximumInstallments'],
})
    .refine((data) => data.maximumTotalExposure === null ||
    data.maximumTotalExposure === undefined ||
    data.maximumTotalExposure >= data.maximumAmount, {
    message: 'Total exposure limit cannot be below the maximum single loan amount.',
    path: ['maximumTotalExposure'],
});
/**
 * A guarantor named on an application.
 *
 * Contact details, not an account reference. Most guarantors have never used
 * this system — UNIFEMGA existed long before it — and the borrower is expected
 * to have spoken to them already. An officer telephones each one to confirm.
 */
export const applicationGuarantorSchema = z.object({
    fullName: z
        .string()
        .trim()
        .min(3, "Enter the guarantor's full name.")
        .max(160),
    relationship: z
        .string()
        .trim()
        .min(2, 'How do you know them?')
        .max(80),
    phone: phoneSchema,
    alternatePhone: z.union([phoneSchema, z.literal('')]).optional(),
    email: z.union([emailSchema, z.literal('')]).optional(),
    address: z.string().trim().min(5, 'Enter their address.').max(255),
    occupation: z.string().trim().max(120).optional(),
    employerName: z.string().trim().max(150).optional(),
    /// Only when the guarantor is themselves a UNIFEMGA member.
    membershipNumber: z.union([membershipNumberSchema, z.literal('')]).optional(),
});
/** What a member fills in on the apply form (§14). */
export const loanApplicationSchema = z.object({
    loanProductId: uuidSchema,
    requestedAmount: positiveNairaAmountSchema,
    installmentCount: z.coerce.number().int().min(1).max(600),
    purpose: z.string().trim().min(10, 'Tell us what the loan is for.').max(1000),
    expectedDisbursementDate: isoDateSchema.optional(),
    // --- Affordability ---
    // Re-asked rather than read from KYC: a KYC record may be two years old, and
    // what matters here is the applicant's circumstances today.
    employmentStatus: z.nativeEnum(EmploymentStatus),
    employerName: z.string().trim().max(150).optional(),
    monthlyIncomeBand: z.string().trim().max(60).optional(),
    repaymentSource: z
        .string()
        .trim()
        .min(5, 'Tell us how you plan to repay.')
        .max(500),
    hasOtherLoans: z.boolean().default(false),
    otherObligations: z.string().trim().max(1000).optional(),
    /// Guarantors are supplied with the application, not nominated afterwards.
    guarantors: z.array(applicationGuarantorSchema).max(5).default([]),
});
/**
 * Submission requires an explicit, recorded confirmation (§17). The IP address
 * and user agent are captured server-side, never trusted from the client.
 */
export const submitApplicationSchema = z.object({
    applicationId: uuidSchema,
    confirmationAccepted: z.literal(true, {
        errorMap: () => ({ message: 'You must confirm before submitting.' }),
    }),
    acceptedTermsVersion: z.string().min(1),
});
/**
 * An officer recording the outcome of telephoning a guarantor.
 *
 * This is the consent record. There is no in-app acceptance to fall back on, so
 * who made the call and what was said is the whole evidentiary basis of the
 * guarantee.
 */
export const confirmGuarantorSchema = z
    .object({
    guarantorId: uuidSchema,
    outcome: z.enum(['CONFIRMED', 'UNREACHABLE', 'DECLINED']),
    /// The number actually reached, which may differ from the one given.
    contactedOn: z.union([phoneSchema, z.literal('')]).optional(),
    callNotes: z.string().trim().max(1000).optional(),
    declineReason: z.string().trim().max(500).optional(),
})
    .refine((data) => data.outcome !== 'CONFIRMED' || Boolean(data.contactedOn), {
    message: 'Record the number you reached them on.',
    path: ['contactedOn'],
})
    .refine((data) => data.outcome !== 'DECLINED' || Boolean(data.declineReason?.trim()), {
    message: 'Record why they declined.',
    path: ['declineReason'],
});
export const applicationDecisionSchema = z
    .object({
    applicationId: uuidSchema,
    decision: z.enum(['APPROVE', 'REJECT', 'REQUEST_INFORMATION']),
    /** May differ from the requested amount — approving less is common. */
    approvedAmount: positiveNairaAmountSchema.optional(),
    approvedInstallmentCount: z.coerce.number().int().min(1).max(600).optional(),
    firstDueDate: isoDateSchema.optional(),
    reason: z.string().trim().max(1000).optional(),
    internalNotes: z.string().trim().max(2000).optional(),
})
    .refine((data) => data.decision !== 'APPROVE' || data.approvedAmount !== undefined, {
    message: 'Specify the amount being approved.',
    path: ['approvedAmount'],
})
    .refine((data) => data.decision === 'APPROVE' || Boolean(data.reason?.trim()), {
    message: 'A reason is required when rejecting or requesting more information.',
    path: ['reason'],
});
export const disbursementSchema = z.object({
    loanId: uuidSchema,
    disbursedAmount: positiveNairaAmountSchema,
    disbursementDate: pastDateSchema,
    paymentMethod: z.nativeEnum(PaymentMethod),
    bankName: z.string().trim().max(100).optional(),
    accountNumber: z.union([accountNumberSchema, z.literal('')]).optional(),
    accountName: z.string().trim().max(120).optional(),
    /** Bank reference. Unique per disbursement — the duplicate-payment guard (§18). */
    transactionReference: z.string().trim().min(3, 'Enter the transaction reference.').max(100),
    notes: z.string().trim().max(1000).optional(),
});
export const recordRepaymentSchema = z.object({
    loanId: uuidSchema,
    amount: positiveNairaAmountSchema,
    paymentDate: pastDateSchema,
    paymentMethod: z.nativeEnum(PaymentMethod),
    transactionReference: z.string().trim().max(100).optional(),
    notes: z.string().trim().max(1000).optional(),
    /**
     * How to treat money beyond the outstanding balance. Defaults to rejecting,
     * because an unexplained surplus is nearly always a data-entry error.
     */
    surplusHandling: z.enum(['REJECT', 'RECORD_AS_CREDIT']).default('REJECT'),
});
export const reverseRepaymentSchema = z.object({
    repaymentId: uuidSchema,
    reason: z.string().trim().min(5, 'Give a reason for the reversal.').max(1000),
});
export const loanSearchSchema = searchSchema.extend({
    status: z.nativeEnum(LoanStatus).optional(),
    loanProductId: uuidSchema.optional(),
    memberId: uuidSchema.optional(),
    overdueOnly: z.coerce.boolean().optional(),
    disbursedFrom: z.coerce.date().optional(),
    disbursedTo: z.coerce.date().optional(),
    minAmount: nairaAmountSchema.optional(),
    maxAmount: nairaAmountSchema.optional(),
});
export const applicationSearchSchema = searchSchema.extend({
    status: z.nativeEnum(LoanApplicationStatus).optional(),
    loanProductId: uuidSchema.optional(),
    submittedFrom: z.coerce.date().optional(),
    submittedTo: z.coerce.date().optional(),
});
export const disbursementSearchSchema = searchSchema.extend({
    status: z.nativeEnum(DisbursementStatus).optional(),
    paymentMethod: z.nativeEnum(PaymentMethod).optional(),
});
/** Query behind the member-facing repayment calculator. */
export const schedulePreviewSchema = z.object({
    loanProductId: uuidSchema,
    amount: positiveNairaAmountSchema,
    installmentCount: z.coerce.number().int().min(1).max(600),
    expectedDisbursementDate: isoDateSchema.optional(),
});
/**
 * A member telling us they have paid.
 *
 * The amount is what they say they sent, not what is due — a member may pay
 * more or less than an installment, and correcting the figure for them would
 * hide the discrepancy an officer needs to see.
 */
export const declareRepaymentSchema = z.object({
    loanId: uuidSchema,
    amount: positiveNairaAmountSchema,
    paymentDate: pastDateSchema,
    /** `HH:mm`, as an `<input type="time">` gives it. Optional. */
    paymentTime: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Enter a time as HH:MM.')
        .optional(),
    paymentMethod: z.nativeEnum(PaymentMethod),
    /**
     * The bank's reference, if the member has it to hand.
     *
     * Optional because most people repaying from a phone do not, and demanding
     * it stopped them telling us at all. Without one an officer matches on
     * amount and date, which is slower — a receipt upload would close that gap
     * once object storage is configured.
     */
    transactionReference: z.string().trim().max(100).optional(),
    note: z.string().trim().max(1000).optional(),
    /**
     * Send again past the duplicate check.
     *
     * Two identical transfers on one day are unusual but real, and the member is
     * the only one who knows. The check warns; it does not decide.
     */
    allowDuplicate: z.boolean().optional(),
    /**
     * The uploaded receipt, set by the browser once the file is in object
     * storage. Required: a declaration without one is a claim with nothing
     * behind it, and an officer would be left matching it to a bank statement
     * on amount and date alone.
     */
    receiptKey: z.string().trim().min(1).max(500),
    receiptFileName: z.string().trim().min(1).max(255),
    receiptMimeType: z.string().trim().min(1).max(100),
});
export const reviewDeclarationSchema = z
    .object({
    declarationId: uuidSchema,
    decision: z.enum(['CONFIRM', 'REJECT']),
    /** Overrides the declared amount when the bank shows something different. */
    confirmedAmount: positiveNairaAmountSchema.optional(),
    reviewNote: z.string().trim().max(1000).optional(),
})
    .refine((data) => data.decision !== 'REJECT' || Boolean(data.reviewNote?.trim()), {
    message: 'Say why this is being rejected — the member is told.',
    path: ['reviewNote'],
});
/** Asks for a signed URL to upload a transfer receipt to. */
export const receiptUploadSchema = z.object({
    fileName: z.string().trim().min(1).max(255),
    mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp', 'application/pdf'], {
        errorMap: () => ({ message: 'Upload a photo or a PDF.' }),
    }),
    fileSize: z
        .number()
        .int()
        .positive()
        .max(10 * 1024 * 1024, 'Receipts must be 10MB or smaller.'),
});
//# sourceMappingURL=loan.js.map